import { isNil } from 'lodash';
import { API } from '@shared/types/api';

// The most cards kept. A collection view can stay open all day while events stream in, so the
// cards seen longest ago make way; a card forgotten this way is treated as one never seen.
const MAX_CARDS = 5000;

export interface KnownCard {
  card: API.WorkflowEntity;
  // whether an event, or a change this client made, reported this version - rather than a list
  // loading it, which says nothing of when the card came to be
  isReported: boolean;
}

interface LastKnownCard {
  known: KnownCard;
  // the version this one replaced, and the event that brought it - so every list handling that
  // same event still reads the version from before it
  replaced?: KnownCard;
  // compared by reference only: it tells which event brought the card, and nothing is read from it
  event?: object;
}

/** A change this client made to a card that a list has already applied, ahead of its event. */
export interface AppliedOwnChange {
  type: API.EntityEventType;
  updatedAt: string;
}

/**
 * Each card's version as the server last reported it: a list's own load of it, then each event for
 * it. Shared by every card list in a collection, so one group can tell where a card stood from
 * another group's load of it.
 *
 * A list as rendered can't stand in for this, because the card an edit's mutation returns goes into
 * the cache - and so into the rendered list - before that edit's event arrives, and the event would
 * then look like no change at all. That's also why a list only fills in cards not already known as
 * it renders, and replaces them only with a fresh fetch from the server.
 */
export class LastKnownCards {
  private readonly cards = new Map<string, LastKnownCard>();
  // by card, then by the list that applied the change - kept here rather than in each list, so a
  // list that remounts before the event arrives still finds the change it applied
  private readonly appliedOwnChanges = new Map<string, Map<string, AppliedOwnChange>>();

  /** Records a card a list rendered, unless a version of it is known already. */
  public load(card: API.WorkflowEntity): void {
    if (!this.cards.has(card.id)) {
      this.set(card.id, { known: { card, isReported: false } });
    }
  }

  /** Records a card a list just fetched from the server, unless a newer version is known. */
  public replace(card: API.WorkflowEntity): void {
    const lastKnown = this.cards.get(card.id);
    if (isNil(lastKnown) || !isNewer(lastKnown.known.card, card)) {
      this.set(card.id, { known: { card, isReported: false } });
    }
  }

  /** The card's version from before `event`, however many lists have handled the event already. */
  public getBefore(cardId: string, event: object): KnownCard | undefined {
    const lastKnown = this.cards.get(cardId);
    if (isNil(lastKnown)) {
      return undefined;
    }
    if (lastKnown.event === event) {
      return lastKnown.replaced;
    }
    // every list has handled the event that brought it by now, so the version it replaced can go
    lastKnown.replaced = undefined;
    return lastKnown.known;
  }

  /** Records the version `event` reported, once however many lists handle the event. */
  public record(card: API.WorkflowEntity, event: object): void {
    const lastKnown = this.cards.get(card.id);
    if (lastKnown?.event === event) {
      return;
    }
    this.set(card.id, {
      known: { card: toKnownVersion(card), isReported: true },
      replaced: lastKnown?.known,
      event,
    });
  }

  /**
   * Records a version the client has already moved the card's lists to - and moved their counts
   * for - ahead of the event that will report it, such as a drag to another group. The event then
   * finds the card already where it belongs and moves nothing again.
   */
  public applyLocalChange(card: API.WorkflowEntity): void {
    this.set(card.id, { known: { card, isReported: true } });
  }

  /** Records that the list `listKey` applied this client's own change to the card. */
  public recordOwnChange(
    listKey: string,
    type: API.EntityEventType,
    card: API.WorkflowEntity,
  ): void {
    const byList = this.appliedOwnChanges.get(card.id) ?? new Map<string, AppliedOwnChange>();
    byList.set(listKey, { type, updatedAt: card.updatedAt });
    this.appliedOwnChanges.delete(card.id);
    this.appliedOwnChanges.set(card.id, byList);
    const oldestCardId = this.appliedOwnChanges.keys().next().value;
    if (this.appliedOwnChanges.size > MAX_CARDS && !isNil(oldestCardId)) {
      this.appliedOwnChanges.delete(oldestCardId);
    }
  }

  /** Whether the list `listKey` already applied the change an event reports for the card. */
  public isOwnChangeApplied(
    listKey: string,
    type: API.EntityEventType,
    card: API.WorkflowEntity,
  ): boolean {
    return isAppliedOwnChange(this.appliedOwnChanges.get(card.id)?.get(listKey), type, card);
  }

  /**
   * Forgets the own change the list `listKey` applied to the card, once an event reports it or a
   * later change. An event for an earlier change leaves it, as its own event is still to come.
   */
  public clearOwnChange(listKey: string, card: API.WorkflowEntity): void {
    const byList = this.appliedOwnChanges.get(card.id);
    const applied = byList?.get(listKey);
    if (isNil(byList) || isNil(applied) || isNewer(applied, card)) {
      return;
    }
    byList.delete(listKey);
    if (byList.size === 0) {
      this.appliedOwnChanges.delete(card.id);
    }
  }

  // a card set again moves to the back of the line, so the cards seen longest ago are dropped first
  private set(cardId: string, lastKnown: LastKnownCard): void {
    this.cards.delete(cardId);
    this.cards.set(cardId, lastKnown);
    const oldestCardId = this.cards.keys().next().value;
    if (this.cards.size > MAX_CARDS && !isNil(oldestCardId)) {
      this.cards.delete(oldestCardId);
    }
  }
}

// A deleted card is in no list, which is all its fields could still tell, so it's kept without them.
function toKnownVersion(card: API.WorkflowEntity): API.WorkflowEntity {
  const deletedAt = card.fields?.deletedAt;
  return isNil(deletedAt) ? card : { ...card, fields: { deletedAt } };
}

/**
 * Whether an event reports an own change already applied: one of the same type, no newer than it.
 * An event older than it reports an earlier change of this client's, which the applied one
 * included. A deletion or restoration leaves `updatedAt` as it was, so its type alone tells it.
 */
export function isAppliedOwnChange(
  applied: AppliedOwnChange | undefined,
  type: API.EntityEventType,
  card: API.WorkflowEntity,
): boolean {
  if (isNil(applied) || applied.type !== type) {
    return false;
  }
  return (
    type === 'WORKFLOW_ENTITY_DELETED' ||
    type === 'WORKFLOW_ENTITY_RESTORED' ||
    !isNewer(card, applied)
  );
}

export function isNewer(card: { updatedAt: string }, other: { updatedAt: string }): boolean {
  return new Date(card.updatedAt).getTime() > new Date(other.updatedAt).getTime();
}
